import type { SessionAcquisition } from '@logclip/types';
import { maskString } from './mask.js';

/**
 * Where this visit came from: the referring site and the landing URL's campaign
 * tags. Read from the page load's URL BEFORE it is redacted (entryUrl drops the
 * whole query string), and once per page — SPA navigations rewrite location.
 *
 * Privacy:
 *  - the referrer is reduced to its HOST. Browsers already send only the origin
 *    cross-site by default; the path/query of someone else's URL (a search, a
 *    token) is never ours to keep.
 *  - ad click ids (gclid, fbclid, …) are reduced to which network sent them.
 *  - utm values are free text; anything that looks like PII is masked.
 */
const UTM = ['source', 'medium', 'campaign', 'term', 'content'] as const;
const CLICK_IDS: Array<[string, NonNullable<SessionAcquisition['adClick']>]> = [
  ['gclid', 'google'], ['gbraid', 'google'], ['wbraid', 'google'], ['dclid', 'google'],
  ['fbclid', 'meta'], ['msclkid', 'microsoft'], ['ttclid', 'tiktok'],
  ['li_fat_id', 'linkedin'], ['twclid', 'x'],
];

export function captureAcquisition(href: string, referrer: string): SessionAcquisition {
  const out: SessionAcquisition = {};
  try {
    if (referrer) out.referrerHost = new URL(referrer).hostname.toLowerCase() || undefined;
  } catch {
    /* not a URL — no referrer */
  }
  try {
    const q = new URL(href).searchParams;
    for (const k of UTM) {
      const v = q.get(`utm_${k}`);
      if (v) out[`utm${k[0]!.toUpperCase()}${k.slice(1)}` as 'utmSource'] = maskString(v).slice(0, 200);
    }
    for (const [param, network] of CLICK_IDS) {
      if (q.has(param)) {
        out.adClick = network;
        break;
      }
    }
  } catch {
    /* unparseable href — no tags */
  }
  if (!out.referrerHost) delete out.referrerHost;
  return out;
}
